import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, CircularProgress, Stack, Typography, Alert, Chip } from '@mui/material';
import apiClient from '../api/client';

interface InitiateResponse {
  autoApproved?: boolean;
  needsConsent?: boolean;
  approveRedirect?: string;
  errorRedirect?: string;
  clientId?: string;
  clientName?: string;
  scopes?: string[];
}

const SCOPE_LABELS: Record<string, string> = {
  'mcp:access': 'Read and update your Cadence data via MCP (work items, plans, calendar, drivers, decisions)',
  'offline_access': 'Stay connected even when you\'re offline (refresh access)',
};

export default function OAuthAuthorizePage() {
  const [phase, setPhase] = useState<'loading' | 'consent' | 'error' | 'submitting'>('loading');
  const [data, setData] = useState<InitiateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Pull every OAuth query parameter — these came from the connector and must
  // be passed through to the backend untouched.
  const params = new URLSearchParams(window.location.search);
  const clientId = params.get('client_id') || '';
  const redirectUri = params.get('redirect_uri') || '';
  const responseType = params.get('response_type') || '';
  const scope = params.get('scope') || '';
  const state = params.get('state') || '';
  const codeChallenge = params.get('code_challenge') || '';
  const codeChallengeMethod = params.get('code_challenge_method') || '';
  // RFC 8707 resource indicator. MCP 2025-06-18 mandates clients send it; the
  // backend persists it on the auth code and binds it onto the access-token
  // audience.
  const resource = params.get('resource') || '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Defence against the live production case where this component is
      // somehow being mounted with no OAuth params. Without this, the page
      // calls initiate with empty fields, gets "Unknown client_id", and
      // the user sees a confusing error. Whatever upstream bug is causing
      // the empty mount, we don't want to make it look like the OAuth flow
      // failed when in fact it never started.
      if (!clientId && !redirectUri && !responseType) {
        if (cancelled) return;
        setError('No OAuth request parameters were supplied. This page expects to be opened by an MCP client (e.g. Claude Desktop) as part of an authorisation flow. If you arrived here directly, return to your MCP client and start the connection again.');
        setPhase('error');
        // Best-effort log so we still see the empty-mount on the server.
        try {
          await apiClient.post('/oauth/authorize/initiate', {
            clientId, redirectUri, responseType, scope, state, codeChallenge, codeChallengeMethod, resource,
            _diagnosticHref: window.location.href,
            _diagnosticSearch: window.location.search,
          });
        } catch { /* ignore — diagnostic only */ }
        return;
      }

      try {
        const { data } = await apiClient.post('/oauth/authorize/initiate', {
          clientId, redirectUri, responseType, scope, state, codeChallenge, codeChallengeMethod, resource,
          // Diagnostic — the cross-origin Referrer-Policy strips path + query
          // from the referer header, so the server-side diagnostic was logging
          // the bare origin and giving us no signal. Echo the full SPA URL +
          // search string back so the missing-fields entry actually tells us
          // what the browser landed on.
          _diagnosticHref: window.location.href,
          _diagnosticSearch: window.location.search,
        });
        if (cancelled) return;
        setData(data);
        if (data.autoApproved && data.approveRedirect) {
          window.location.assign(data.approveRedirect);
          return;
        }
        if (data.errorRedirect) {
          window.location.assign(data.errorRedirect);
          return;
        }
        if (data.needsConsent) {
          setPhase('consent');
          return;
        }
        setError('Unexpected response from authorisation server.');
        setPhase('error');
      } catch (e: any) {
        if (cancelled) return;
        const msg = e?.response?.data?.error_description || e?.response?.data?.error || 'Could not start authorisation.';
        setError(typeof msg === 'string' ? msg : 'Could not start authorisation.');
        setPhase('error');
      }
    })();
    return () => { cancelled = true; };
    // Run once on mount — the OAuth params come from the URL and don't change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const decide = async (approve: boolean) => {
    setPhase('submitting');
    const scopes = (data?.scopes ?? []).join(' ');
    try {
      const path = approve ? '/oauth/authorize/approve' : '/oauth/authorize/deny';
      const { data: result } = await apiClient.post(path, {
        clientId, redirectUri, scope: scopes, state, codeChallenge, codeChallengeMethod, resource,
      });
      if (result?.redirect) {
        window.location.assign(result.redirect);
        return;
      }
      setError('No redirect URL returned.');
      setPhase('error');
    } catch (e: any) {
      const msg = e?.response?.data?.error_description || e?.response?.data?.error || 'Could not record decision.';
      setError(typeof msg === 'string' ? msg : 'Could not record decision.');
      setPhase('error');
    }
  };

  if (phase === 'loading' || phase === 'submitting') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            {phase === 'submitting' ? 'Recording decision…' : 'Checking authorisation…'}
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (phase === 'error') {
    const isUnknownClient = (error || '').toLowerCase().includes('unknown client');
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Authorisation failed.'}</Alert>
        {isUnknownClient && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Diagnostic:</strong> the client the MCP app is using is{' '}
            <code style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{clientId || '(none)'}</code>.
            Cadence has no record of it.
            <br /><br />
            <strong>To fix:</strong> fully quit Claude Desktop (⌘Q on macOS), reopen it,
            then in Settings → Integrations remove the Cadence connector and add it again
            with <code>https://api.stratiumhq.com/mcp</code>. That triggers a fresh
            registration and the next attempt will succeed.
          </Alert>
        )}
        <Button variant="text" onClick={() => { window.location.href = '/'; }}>Back to Cadence</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', mt: 6 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Connect {data?.clientName || 'this app'} to Cadence?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {data?.clientName || 'A connected app'} is requesting access to your Cadence account.
            Review what it will be able to do before approving.
          </Typography>

          <Stack spacing={1.5} sx={{ mb: 3 }}>
            {(data?.scopes ?? []).map(s => (
              <Box key={s} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Chip label={s} size="small" sx={{ mt: 0.25 }} />
                <Typography variant="body2">{SCOPE_LABELS[s] || s}</Typography>
              </Box>
            ))}
          </Stack>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            You can revoke access at any time from Connections → Connected apps.
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => decide(false)} color="inherit">Deny</Button>
            <Button onClick={() => decide(true)} variant="contained">Allow</Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
